/**
 * Hala CRM - Login Page
 * Simple login with username/password for CRM users
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, User, LogIn, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  primary: '#1B3A6B',
  gold: '#C5A572',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
  error: '#EF4444',
  errorLight: '#FEE2E2',
};

export default function CRMLoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/crm-auth/login`, {
        username,
        password
      });

      // Store auth data
      localStorage.setItem('crm_token', res.data.token);
      localStorage.setItem('crm_user', JSON.stringify(res.data));

      // Redirect based on role
      const role = res.data.role_id;
      if (role === 'admin') {
        navigate('/crm/dashboard'); // Admin goes to dashboard (system control)
      } else if (role === 'supervisor') {
        navigate('/crm/supervisor'); // Supervisor goes to monitoring page
      } else if (role === 'consultant') {
        navigate('/crm/my-requests'); // Consultant sees their assigned requests
      } else if (role === 'group_user') {
        navigate('/crm/pool'); // Group users go to pool to claim
      } else {
        navigate('/crm/pool');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'حدث خطأ في تسجيل الدخول');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: COLORS.bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      direction: 'rtl',
    }}>
      <div style={{
        backgroundColor: COLORS.white,
        borderRadius: '20px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
        width: '100%',
        maxWidth: '420px',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          backgroundColor: COLORS.primary,
          padding: '32px',
          textAlign: 'center',
        }}>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 700, color: COLORS.gold }}>
            نظام إدارة الطلبات
          </h1>
          <p style={{ margin: '8px 0 0', fontSize: '14px', color: 'rgba(255,255,255,0.7)' }}>
            هلا
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleLogin} style={{ padding: '32px' }}>
          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 16px',
              backgroundColor: COLORS.errorLight,
              color: COLORS.error,
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '14px',
            }}>
              <AlertCircle size={18} />
              {error}
            </div>
          )}

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: 500,
              color: COLORS.text,
            }}>
              اسم المستخدم
            </label>
            <div style={{ position: 'relative' }}>
              <User size={18} color={COLORS.textMuted} style={{
                position: 'absolute',
                left: '14px',
                top: '50%',
                transform: 'translateY(-50%)',
              }} />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="user01"
                required
                style={{
                  width: '100%',
                  padding: '14px 14px 14px 44px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '10px',
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  textAlign: 'right',
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: 500,
              color: COLORS.text,
            }}>
              كلمة المرور
            </label>
            <div style={{ position: 'relative' }}>
              <Lock size={18} color={COLORS.textMuted} style={{
                position: 'absolute',
                left: '14px',
                top: '50%',
                transform: 'translateY(-50%)',
              }} />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••"
                required
                style={{
                  width: '100%',
                  padding: '14px 14px 14px 44px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '10px',
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  textAlign: 'right',
                }}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: COLORS.primary,
              color: COLORS.white,
              border: 'none',
              borderRadius: '10px',
              fontSize: '16px',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'opacity 0.2s',
            }}
          >
            {loading ? (
              'جاري تسجيل الدخول...'
            ) : (
              <>
                <LogIn size={18} />
                تسجيل الدخول
              </>
            )}
          </button>

          {/* Test Users Info */}
          <div style={{
            marginTop: '24px',
            padding: '16px',
            backgroundColor: COLORS.bg,
            borderRadius: '10px',
            fontSize: '13px',
          }}>
            <p style={{ margin: '0 0 8px', fontWeight: 600, color: COLORS.text }}>
              حسابات الاختبار:
            </p>
            <div style={{ color: COLORS.textMuted, lineHeight: 1.8 }}>
              <p style={{ margin: 0 }}>user01 - مدير النظام</p>
              <p style={{ margin: 0 }}>user02 - مشرف</p>
              <p style={{ margin: 0 }}>user03 - مستشار</p>
              <p style={{ margin: 0 }}>user04 - مستخدم مجموعة</p>
              <p style={{ margin: '8px 0 0', fontWeight: 500, color: COLORS.primary }}>
                كلمة المرور: user
              </p>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
